import {
  Droplet, Activity, Disc, CircleDot, BatteryCharging, LifeBuoy, Armchair, SprayCan, Sparkles, Wrench, Loader2,
} from "lucide-react";
import { STATUS } from "../lib/api";

export const Card = ({ className = "", children, ...p }) => (
  <div className={`bg-[#141414] border border-[#262626] rounded-2xl p-4 ${className}`} {...p}>{children}</div>
);

const VARIANTS = {
  primary: "bg-[#FF6B00] hover:bg-[#E66000] text-white",
  success: "bg-[#10B981] hover:bg-[#059669] text-black",
  outline: "bg-transparent border border-[#333] hover:border-neutral-500 text-neutral-200",
  ghost: "bg-transparent hover:bg-[#1c1c1c] text-neutral-300",
  danger: "bg-transparent border border-red-900 text-red-400 hover:bg-red-950/40",
};

export const Btn = ({ variant = "primary", size = "md", loading, className = "", children, ...p }) => (
  <button
    className={`inline-flex items-center justify-center gap-2 rounded-xl font-semibold transition-[background-color,border-color,transform] active:scale-[0.98] disabled:opacity-40 disabled:pointer-events-none ${
      size === "lg" ? "h-14 px-5 text-base" : size === "sm" ? "h-9 px-3 text-sm" : "h-11 px-4 text-sm"
    } ${VARIANTS[variant]} ${className}`}
    disabled={loading || p.disabled}
    {...p}
  >
    {loading && <Loader2 className="w-4 h-4 animate-spin" />}
    {children}
  </button>
);

export const inputCls =
  "w-full bg-[#0F0F0F] border border-[#262626] focus:border-[#FF6B00] text-white px-3.5 h-11 rounded-xl outline-none text-[15px] placeholder:text-neutral-600 transition-colors";

export const Input = ({ className = "", ...p }) => <input className={`${inputCls} ${className}`} {...p} />;
export const Textarea = ({ className = "", ...p }) => (
  <textarea className={`${inputCls} h-auto py-2.5 min-h-[84px] ${className}`} {...p} />
);
export const Select = ({ className = "", children, ...p }) => (
  <select className={`${inputCls} ${className}`} {...p}>{children}</select>
);

export const Field = ({ label, hint, children, className = "" }) => (
  <label className={`block ${className}`}>
    <span className="block text-xs uppercase tracking-wider text-neutral-500 mb-1.5">{label}</span>
    {children}
    {hint && <span className="block text-xs text-neutral-500 mt-1">{hint}</span>}
  </label>
);

export const Section = ({ title, icon: Icon, right, children, testid }) => (
  <Card className="space-y-3" data-testid={testid}>
    <div className="flex items-center justify-between gap-2">
      <h3 className="font-head text-lg font-semibold uppercase tracking-wide flex items-center gap-2">
        {Icon && <Icon className="w-4 h-4 text-[#FF6B00]" />}
        {title}
      </h3>
      {right}
    </div>
    {children}
  </Card>
);

const CHIP = {
  draft: "border-neutral-700 text-neutral-400", open: "border-amber-700 text-amber-400",
  assigned: "border-blue-800 text-blue-400", en_route: "border-blue-800 text-blue-400",
  before_check: "border-orange-800 text-orange-400", working: "border-orange-700 text-[#FF6B00]",
  after_check: "border-orange-800 text-orange-400", awaiting_review: "border-purple-800 text-purple-300",
  completed: "border-emerald-800 text-emerald-400", cancelled: "border-red-900 text-red-400",
  needs: "border-red-700 text-red-400 bg-red-950/30",
};

export const StatusChip = ({ status, needs, testid }) => (
  <span data-testid={testid} className={`px-2.5 py-1 rounded-full text-[11px] font-mono border uppercase tracking-wider whitespace-nowrap ${CHIP[needs ? "needs" : status] || ""}`}>
    {needs ? "Needs dispatch" : STATUS[status] || status}
  </span>
);

const ICONS = {
  oil_change: Droplet, diagnostics: Activity, brake_pads_axle: Disc, pads_rotors_axle: Disc, calipers: CircleDot,
  battery: BatteryCharging, spare_tire: LifeBuoy, tire_service: LifeBuoy, interior_detail: Armchair,
  exterior_detail: SprayCan, full_detail: Sparkles,
};

export const ServiceIcon = ({ k, className = "w-5 h-5" }) => {
  const I = ICONS[k] || Wrench;
  return (
    <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-[#1d1d1d] border border-[#2a2a2a] text-[#FF6B00] shrink-0">
      <I className={className} />
    </span>
  );
};

export const Money = ({ v, green, className = "", testid }) => (
  <span data-testid={testid} className={`font-mono ${green ? "text-[#10B981]" : ""} ${className}`}>${Number(v || 0).toFixed(2)}</span>
);

export const Empty = ({ icon: Icon, title, text }) => (
  <div className="text-center py-14 px-6 text-neutral-500">
    {Icon && <Icon className="w-8 h-8 mx-auto mb-3 text-neutral-600" />}
    <div className="text-neutral-300 font-medium">{title}</div>
    {text && <div className="text-sm mt-1">{text}</div>}
  </div>
);

export const Row = ({ k, v, testid }) => (
  <div className="flex justify-between gap-4 text-sm py-1">
    <span className="text-neutral-500">{k}</span>
    <span className="text-right text-neutral-200" data-testid={testid}>{v}</span>
  </div>
);
